import { useState } from 'react';
import type { AdminCustomer, AdminSettingsSnapshot, CustomerLimitPatch } from './adminApi';

/**
 * Per-customer tier and fair-use controls (D-32).
 *
 * Fair use is one global number, which is right for paying customers and wrong
 * for the operator's own accounts: tightening the Pro allowance immediately
 * locked out the account being used to test the product, and the only way back
 * in was editing the database by hand. An empty field means "use the global
 * setting" — the override is opt-in per account, and `unlimited` is a separate
 * flag rather than a huge number so it is obvious which accounts are not metered.
 */
export default function LimitEditor({ c, defaults, onClose, onSave }: {
  c: AdminCustomer;
  defaults: AdminSettingsSnapshot | null;
  onClose: () => void;
  onSave: (patch: CustomerLimitPatch) => void | Promise<void>;
}): JSX.Element {
  const [tier, setTier] = useState<'free' | 'pro'>(c.tier === 'pro' ? 'pro' : 'free');
  const [day, setDay] = useState(c.probe_per_day == null ? '' : String(c.probe_per_day));
  const [month, setMonth] = useState(c.probe_per_month == null ? '' : String(c.probe_per_month));
  const [unlimited, setUnlimited] = useState(c.limits_unlimited);
  const [note, setNote] = useState(c.note ?? '');
  const [reset, setReset] = useState(false);
  const [busy, setBusy] = useState(false);

  const num = (v: string): number | null => (v.trim() === '' ? null : Math.max(0, Number(v) || 0));

  async function save(): Promise<void> {
    setBusy(true);
    await onSave({
      tier,
      probePerDay: num(day),
      probePerMonth: num(month),
      unlimited,
      note: note.trim() || null,
      resetCounters: reset,
    });
    setBusy(false);
  }

  const field = 'bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1.5 font-mono text-sm outline-none';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-warm-dark border border-warm-dark-2 rounded-xl p-5 my-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-serif text-xl">{c.email}</h3>
            <p className="font-mono text-xs text-text-secondary">
              used {c.used_today} today · {c.used_month} this month · {c.scans_total} runs lifetime
            </p>
          </div>
          <button onClick={onClose} className="font-mono text-sm text-text-secondary hover:text-cyan">✕ close</button>
        </div>

        <div className="mt-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/70 mb-1.5">Plan</p>
          <div className="flex gap-2">
            {(['free', 'pro'] as const).map((t) => (
              <button key={t} onClick={() => setTier(t)}
                className={`font-mono text-sm rounded-lg px-4 py-2 border transition ${
                  tier === t ? 'bg-cyan text-warm-dark border-cyan' : 'bg-[#26262F] text-text-primary border-[#70707D] hover:border-cyan/60'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/70 mb-1.5">Fair use</p>
          <label className="flex items-center gap-2 text-sm text-text-primary mb-2">
            <input type="checkbox" checked={unlimited} onChange={() => setUnlimited((v) => !v)} className="accent-cyan" />
            No limits at all, for internal / test accounts
          </label>
          <div className={`grid grid-cols-2 gap-3 ${unlimited ? 'opacity-40 pointer-events-none' : ''}`}>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-xs text-text-secondary">Runs per day</span>
              <input value={day} onChange={(e) => setDay(e.target.value)} inputMode="numeric"
                placeholder={`default ${defaults?.proProbePerDay ?? '-'}`} className={field} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-xs text-text-secondary">Runs per month</span>
              <input value={month} onChange={(e) => setMonth(e.target.value)} inputMode="numeric"
                placeholder={`default ${defaults?.proProbePerMonth ?? '-'}`} className={field} />
            </label>
          </div>
          <p className="text-sm text-text-secondary mt-1.5">
            Leave a field empty to use the global setting. A run costs up to <span className="font-mono">$0.75</span>,
            so a monthly allowance of {defaults?.proProbePerMonth ?? '-'} is what keeps a €20 account profitable,
            so raise it deliberately.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-text-primary mt-4">
          <input type="checkbox" checked={reset} onChange={() => setReset((v) => !v)} className="accent-cyan" />
          Also reset this account&apos;s counters now
        </label>
        <p className="text-sm text-text-secondary mt-1">
          Clears today&apos;s and this month&apos;s usage so a locked-out account can carry on immediately.
        </p>

        <label className="flex flex-col gap-1 mt-4">
          <span className="font-mono text-xs text-text-secondary">Note (shown in the customers table)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. internal test account" className={field} />
        </label>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-warm-dark-2">
          <button onClick={onClose} className="font-mono text-sm text-text-secondary hover:text-cyan px-3 py-2">cancel</button>
          <button onClick={() => void save()} disabled={busy}
            className="font-mono text-sm bg-cyan text-warm-dark rounded-lg px-5 py-2 disabled:opacity-50 hover:opacity-90 transition">
            {busy ? 'saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
